import * as React from 'react'
import Select from 'react-select'
import { Button, Modal, Input, useModalContext } from 'src/design-system'
import { SkillsService } from 'app/packs/src/services/skills-service'
import { useApiClient } from 'app/packs/src/utils/use-api-client'
import { useRailsContext } from 'app/packs/src/components/rails-context'
import { successToast } from 'app/packs/src/utils/success-toast'
import { errorToast } from 'app/packs/src/utils/error-toast'
import { store } from 'store/index'
import { deserialize } from '../../api/utils/deserialize'
import { Info, Warning } from '@phosphor-icons/react'

interface SkillRequirementMatchDialogProps {
  defaultOpen: boolean
  onOpenChange: (open: boolean) => void
  skillId: string
  targetSkillName: string

  source?: string
}

export const SkillRequirementMatchDialog: React.FC<
  SkillRequirementMatchDialogProps
> = ({ defaultOpen, onOpenChange, skillId, targetSkillName }) => {
  const [selectedSkillId, setSelectedSkillId] = React.useState<string | null>(
    null
  )

  const [loading, setLoading] = React.useState(false)
  const [skill, setSkill] = React.useState<{
    id: string
    name: string
    teamCount?: number
    positionCount?: number
  } | null>(null)

  const [allskills, setAllSkills] = React.useState<
    {
      id: string
      name: string
      teamCount: number
      positionCount: number
      teamIds?: string[]
    }[]
  >([])

  const [selectedSkillTeamIds, setSelectedSkillTeamIds] = React.useState<
    string[]
  >([])

  const [targetSkillTeamIds, setTargetSkillTeamIds] = React.useState<string[]>(
    []
  )

  const client = useApiClient()
  const { org } = useRailsContext()
  const service = React.useMemo(
    () => new SkillsService(client, org ?? undefined),
    [client, org]
  )
  // Fetch skill details when a skill is selected
  React.useEffect(() => {
    const fetchSkillDetails = async () => {
      if (selectedSkillId) {
        const selectedSkill = allskills.find((s) => s.id === selectedSkillId)

        try {
          await store.skills.fetchOne(selectedSkillId)
          const skill = store.skills.data.get(selectedSkillId)
          if (skill) {
            setSkill({
              ...skill,
              teamCount: selectedSkill?.teamCount ?? 0,
              positionCount: selectedSkill?.positionCount ?? 0,
            })
            setSelectedSkillTeamIds(selectedSkill?.teamIds ?? [])
          }
        } catch {
          errorToast('Failed to fetch skill details')
        }
      }
    }

    fetchSkillDetails()
  }, [selectedSkillId, allskills])

  // Fetch skill insights
  const fetchSkillInsights = React.useCallback(async () => {
    try {
      const res = await fetch('/api/v1/skill_insights')
      if (!res.ok) return []
      const json = await res.json()
      const deserialized = deserialize(json)

      if (!deserialized?.data) return []

      const insights = deserialized.data.skillInsights as Record<
        string,
        {
          id: string
          name: string
          teamCount: number
          positionCount: number
          teamIds: string[]
        }
      >

      const skillData = Object.values(insights)
        .map((insight) => ({
          id: insight.id,
          name: insight.name,
          teamCount: insight.teamCount,
          positionCount: insight.positionCount,
          teamIds: insight.teamIds,
        }))
        .filter((skill) => skill.id !== skillId)

      const targetSkill = insights[skillId]
      const targetSkillTeamIds = targetSkill?.teamIds ?? []

      setAllSkills(skillData)
      setTargetSkillTeamIds(targetSkillTeamIds)
      return skillData
    } catch (error) {
      console.error('Failed to fetch skill insights', error)
      return []
    }
  }, [skillId])

  // Fetch insights on component mount
  React.useEffect(() => {
    if (defaultOpen) {
      fetchSkillInsights()
    } else {
      setSelectedSkillId(null)
      setSkill(null)
      setSelectedSkillTeamIds([])
    }
  }, [defaultOpen, fetchSkillInsights])

  const hasOverlappingTeams = selectedSkillTeamIds.some((id) =>
    targetSkillTeamIds.includes(id)
  )

  // Check if selected skill has no teams and no positions
  const hasNoTeamsOrPositions = skill
    ? skill.teamCount === 0 && skill.positionCount === 0
    : false

  const handleMatchRequirement = async () => {
    if (!selectedSkillId) return

    setLoading(true)
    try {
      const response = await service.requirementMatch({
        skill_id: skillId,
        selected_skill_id: selectedSkillId,
      })

      if (response.success) {
        await store.skills.fetchOne(skillId)
        successToast(
          `Requirements for ${targetSkillName} successfully matched.`
        )
        onOpenChange(false)
      } else {
        errorToast('Failed to match requirements.')
      }
    } catch (error) {
      errorToast('An error occurred while matching requirements.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal.Root
      open={defaultOpen}
      onOpenChange={onOpenChange}
      title={targetSkillName || 'Match requirements'}
    >
      <div className="p-6 w-full max-w-2xl h-[350px] bg-white rounded-xl shadow-lg">
        <h1 className="text-xl font-semibold">Match requirements of...</h1>
        <p className="text-gray-600 mt-2">
          This will add {targetSkillName} to any teams and positions where the
          same skill level is available. Existing usage of the skill will not be
          changed.
        </p>

        <div className="mt-4 space-y-4">
          {/* Skill Dropdown */}
          <div>
            <label className="block text-sm  font-semibold">Skill</label>
            <Select
              options={allskills.map((skill) => ({
                value: skill.id,
                label: `${skill.name}`,
              }))}
              value={
                allskills.find((s) => s.id === selectedSkillId)
                  ? {
                      value:
                        allskills.find((s) => s.id === selectedSkillId)?.id ??
                        '',
                      label:
                        allskills.find((s) => s.id === selectedSkillId)?.name ??
                        '',
                    }
                  : null
              }
              onChange={(selectedOption) =>
                setSelectedSkillId(selectedOption ? selectedOption.value : null)
              }
              isDisabled={loading}
              className="mt-1"
              classNamePrefix="react-select"
              placeholder="Choose a skill to match requirements"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 100,
                }),
              }}
            />
          </div>
        </div>
        {/* Display skill details after selection */}
        {skill && !hasNoTeamsOrPositions && (
          <div className="mt-4 p-3 bg-blue-100 text-blue-700 rounded-md flex items-start">
            <Info
              className="mr-2 mt-0.5 flex-shrink-0"
              weight="bold"
              size={16}
            />
            <p className="text-sm">
              {targetSkillName} will be added to {skill.teamCount} teams and{' '}
              {skill.positionCount} positions. It won&apos;t be removed from any
              existing positions.
            </p>
          </div>
        )}

        {skill && hasNoTeamsOrPositions && (
          <div className="mt-4 p-3 bg-yellow-100 text-yellow-700 rounded-md flex items-start">
            <Warning
              className="mr-2 mt-0.5 flex-shrink-0"
              weight="bold"
              size={16}
            />
            <p className="text-sm">
              Warning: The {skill.name} is not associated with any teams or
              positions. Matching requirements is not possible.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleMatchRequirement}
            disabled={loading || !selectedSkillId || hasNoTeamsOrPositions}
          >
            Match requirements
          </Button>
        </div>
      </div>
    </Modal.Root>
  )
}
